import React, { useState } from "react";
import Header from "parts/Header";
import Footer from "parts/Footer";
import { Fade } from "react-awesome-reveal";
import Discuss from "parts/Discuss";
// eslint-disable-next-line import/no-extraneous-dependencies
import {
  Shield,
  Scale,
  FileText,
  Lock,
  CheckCircle,
  Globe,
  Award,
  TrendingUp,
  ArrowRight,
  Download,
} from "lucide-react";

const LegalAndCompliancePage = () => {
  const [hoveredCard, setHoveredCard] = useState(null);

  const complianceAreas = [
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Regulatory Excellence",
      description:
        "Stay ahead of evolving regulations with our AI-powered compliance monitoring and real-time alerts across 50+ jurisdictions",
      features: [
        "SEC Compliance",
        "GDPR & Privacy",
        "International Standards",
        "Risk Assessment",
      ],
      color: "from-blue-500 to-cyan-500",
      metric: "99.9% Compliance Rate",
    },
    {
      icon: <Scale className="w-8 h-8" />,
      title: "Legal Framework",
      description:
        "Comprehensive legal infrastructure designed for rapid scaling while maintaining bulletproof protection",
      features: [
        "Contract Automation",
        "IP Protection",
        "M&A Support",
        "Dispute Resolution",
      ],
      color: "from-purple-500 to-pink-500",
      metric: "500+ Legal Docs",
    },
    {
      icon: <FileText className="w-8 h-8" />,
      title: "Documentation Hub",
      description:
        "Centralized, blockchain-verified document management with smart contracts and automated workflows",
      features: [
        "Smart Contracts",
        "Audit Trails",
        "Version Control",
        "Digital Signatures",
      ],
      color: "from-green-500 to-emerald-500",
      metric: "100% Digital",
    },
    {
      icon: <Lock className="w-8 h-8" />,
      title: "Data Security",
      description:
        "Military-grade encryption and zero-trust architecture protecting sensitive information and client data",
      features: [
        "End-to-End Encryption",
        "Multi-Factor Auth",
        "Breach Prevention",
        "Secure Backup",
      ],
      color: "from-orange-500 to-red-500",
      metric: "Zero Breaches",
    },
  ];

  const certifications = [
    { name: "SOC 2 Type II", icon: <Award className="w-6 h-6" /> },
    { name: "ISO 27001", icon: <Shield className="w-6 h-6" /> },
    { name: "GDPR Certified", icon: <Globe className="w-6 h-6" /> },
    { name: "SEC Registered", icon: <Scale className="w-6 h-6" /> },
  ];

  const stats = [
    {
      number: "100%",
      label: "Compliance Record",
      icon: <CheckCircle className="w-6 h-6" />,
    },
    {
      number: "24/7",
      label: "Monitoring",
      icon: <Shield className="w-6 h-6" />,
    },
    {
      number: "50+",
      label: "Jurisdictions",
      icon: <Globe className="w-6 h-6" />,
    },
    {
      number: "99.9%",
      label: "Uptime SLA",
      icon: <TrendingUp className="w-6 h-6" />,
    },
  ];

  return (
    <>
      <Header />
      <div className="min-h-screen mb-16 overflow-hidden bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900">
        {/* Hero Section */}
        <div className="container relative z-10 px-6 pt-20 pb-16 mx-auto ">
          <Fade cascade triggerOnce>
            <div className="mb-16 text-center">
              <div className="inline-flex items-center px-6 py-3 mb-8 border rounded-full bg-gradient-to-r from-blue-600/20 to-purple-600/20 backdrop-blur-sm border-white/10">
                <Shield className="w-5 h-5 mr-2 text-cyan-400 animate-pulse" />
                <span className="text-sm font-medium text-white/90">
                  Fortress-Level Protection
                </span>
              </div>

              <h1 className="mb-6 text-6xl font-black leading-tight text-transparent md:text-8xl bg-clip-text bg-gradient-to-r from-white via-cyan-200 to-blue-200">
                Legal
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">
                  Excellence
                </span>
              </h1>

              <p className="max-w-3xl mx-auto mb-8 text-xl leading-relaxed text-white/70">
                Navigate complex regulatory landscapes with confidence. Our
                AI-powered compliance suite and legal framework provide
                bulletproof protection for tomorrow&apos;s unicorns.
              </p>

              <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
                <button
                  type="button"
                  className="flex items-center px-8 py-4 font-semibold text-white transition-all duration-300 transform rounded-full group bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 hover:scale-105 hover:shadow-2xl"
                >
                  <Download className="w-5 h-5 mr-2 group-hover:animate-bounce" />
                  Compliance Checklist
                </button>
                <button
                  type="button"
                  className="flex items-center px-8 py-4 font-semibold text-white transition-all duration-300 border rounded-full group bg-white/10 backdrop-blur-sm border-white/20 hover:bg-white/20"
                >
                  Legal Consultation
                  <ArrowRight className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" />
                </button>
              </div>
            </div>
          </Fade>
        </div>

        {/* Stats Section */}
        <div className="container relative z-10 px-6 mx-auto mb-20">
          <Fade triggerOnce>
            <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
              {stats.map((stat, index) => (
                <div
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  className="p-6 text-center transition-all duration-300 border group bg-white/5 backdrop-blur-sm border-white/10 rounded-2xl hover:bg-white/10 hover:scale-105"
                >
                  <div className="flex justify-center mb-3 text-cyan-400 group-hover:animate-bounce">
                    {stat.icon}
                  </div>
                  <div className="mb-1 text-3xl font-bold text-white">
                    {stat.number}
                  </div>
                  <div className="text-sm text-white/60">{stat.label}</div>
                </div>
              ))}
            </div>
          </Fade>
        </div>

        {/* Certifications Banner */}
        <div className="container relative z-10 px-6 mx-auto mb-20">
          <Fade triggerOnce>
            <div className="p-8 border bg-gradient-to-r from-white/5 to-white/10 backdrop-blur-sm border-white/10 rounded-3xl">
              <div className="mb-8 text-center">
                <h3 className="mb-2 text-2xl font-bold text-white">
                  Certified & Trusted
                </h3>
                <p className="text-white/70">
                  Industry-leading certifications and compliance standards
                </p>
              </div>
              <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
                {certifications.map((cert, index) => (
                  <div
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    className="flex flex-col items-center transition-transform duration-300 group hover:scale-110"
                  >
                    <div className="p-4 mb-3 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-2xl group-hover:animate-pulse">
                      {cert.icon}
                    </div>
                    <span className="text-sm font-medium text-center text-white/80">
                      {cert.name}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </Fade>
        </div>

        {/* Interactive Compliance Areas */}
        <div className="container relative z-10 px-6 mx-auto mb-20">
          <Fade triggerOnce>
            <div className="mb-12 text-center">
              <h2 className="mb-4 text-4xl font-bold text-white md:text-5xl">
                Complete{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">
                  Protection Suite
                </span>
              </h2>
              <p className="max-w-2xl mx-auto text-lg text-white/70">
                Four pillars of legal excellence designed to scale with your
                ambitious growth
              </p>
            </div>

            <div className="grid max-w-6xl gap-8 mx-auto md:grid-cols-2">
              {complianceAreas.map((area, index) => (
                <div
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  className={`group relative bg-gradient-to-br ${
                    area.color
                  } p-1 rounded-3xl transition-all duration-500 hover:scale-105 cursor-pointer ${
                    hoveredCard === index ? "scale-105" : ""
                  }`}
                  onMouseEnter={() => setHoveredCard(index)}
                  onMouseLeave={() => setHoveredCard(null)}
                >
                  <div className="h-full p-8 bg-slate-900/90 backdrop-blur-sm rounded-3xl">
                    <div className="flex items-center mb-6">
                      <div className="mr-4 text-white group-hover:animate-pulse">
                        {area.icon}
                      </div>
                      <div>
                        <h3 className="mb-1 text-xl font-bold text-white">
                          {area.title}
                        </h3>
                        <div className="px-3 py-1 rounded-full bg-white/10 backdrop-blur-sm">
                          <span className="text-xs font-medium text-white/80">
                            {area.metric}
                          </span>
                        </div>
                      </div>
                    </div>

                    <p className="mb-6 leading-relaxed text-white/70">
                      {area.description}
                    </p>

                    <div className="mb-6 space-y-3">
                      {area.features.map((feature, idx) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <div key={idx} className="flex items-center">
                          <CheckCircle className="flex-shrink-0 w-4 h-4 mr-3 text-green-400" />
                          <span className="text-sm text-white/80">
                            {feature}
                          </span>
                        </div>
                      ))}
                    </div>

                    <button
                      type="button"
                      className="flex items-center justify-center w-full py-3 font-medium text-white transition-all duration-300 border bg-white/10 hover:bg-white/20 backdrop-blur-sm border-white/20 rounded-xl group"
                    >
                      Learn More
                      <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </Fade>
        </div>

        {/* CTA Section */}
        <div className="container relative z-10 px-6 pb-20 mx-auto">
          <Fade triggerOnce>
            <div className="p-12 text-center border bg-gradient-to-r from-cyan-600/20 to-blue-600/20 backdrop-blur-sm border-white/10 rounded-3xl">
              <div className="flex justify-center mb-6">
                <div className="p-4 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-2xl">
                  <Shield className="w-8 h-8 text-white animate-pulse" />
                </div>
              </div>

              <h3 className="mb-4 text-3xl font-bold text-white md:text-4xl">
                Bulletproof Your Business
              </h3>

              <p className="max-w-2xl mx-auto mb-8 text-lg text-white/70">
                Don&apos;t let legal complexities slow your growth. Get
                enterprise-grade compliance infrastructure from day one.
              </p>

              <div className="flex flex-col justify-center gap-4 sm:flex-row">
                <button
                  type="button"
                  className="flex items-center justify-center px-10 py-4 font-semibold text-white transition-all duration-300 transform rounded-full group bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 hover:scale-105 hover:shadow-2xl"
                >
                  Get Protected Now
                  <Shield className="w-5 h-5 ml-2 group-hover:animate-pulse" />
                </button>
                <button
                  type="button"
                  className="flex items-center justify-center px-10 py-4 font-semibold text-white transition-all duration-300 border rounded-full group bg-white/10 backdrop-blur-sm border-white/20 hover:bg-white/20"
                >
                  Schedule Legal Review
                </button>
              </div>
            </div>
          </Fade>
        </div>
      </div>
      <Discuss />
      <Footer />
    </>
  );
};

export default LegalAndCompliancePage;
